/**
 * The Activity log's URL view state (DNKADM-0430 P1-C).
 *
 * Split from `ActivityLog.tsx` so the component file exports components only and the
 * param rules are testable alone.
 *
 * URL params on `/activity` (all optional; a default is never written):
 * - `q`: the text search. It is CLIENT side and covers only the loaded page (there is no
 *   server text search), so changing it never resets `page`.
 * - `kind`: the Type filter, one exact action (`?kind=` on the wire). Missing = all.
 * - `member`: the member filter's contact id (`?contactId=` on the wire).
 * - `from` / `to`: the date range, `YYYY-MM-DD` (`?fromDate=` / `?toDate=` on the wire).
 * - `page`: 1-indexed.
 * Any server-filter change resets `page`. The list is server paged and has no sort in
 * Phase 1 (server sort waits on DNKAPI-0615); the location scope comes from the app bar,
 * not the URL.
 */

/** Every param the Activity log keeps in the URL, with its default. */
export const ACTIVITY_URL_DEFAULTS = { q: '', kind: '', member: '', from: '', to: '', page: '1' };

/** The Type select's "All activity" sentinel (`ActivityActionFilter`); omits `?kind=`. */
export const ALL_KINDS = 'all';

/**
 * A `kind` value shaped like an action token (`reservation_cancel`). The vocabulary is
 * fetched, so the check is on shape, not membership: a kind the live list knows but the
 * fallback does not must survive the first render.
 */
const KIND_TOKEN = /^[A-Za-z0-9_.:-]{1,64}$/;

/** Read the `kind` param as the Type select's value: a token, else {@link ALL_KINDS}. */
export function parseKindParam(raw: string): string {
  return KIND_TOKEN.test(raw) ? raw : ALL_KINDS;
}

/** The `kind` param for a Type select value: `null` (cleared) for All. */
export function toKindParam(value: string): string | null {
  return value === ALL_KINDS ? null : value;
}

/** A contact id the member filter will send. */
const CONTACT_ID = /^[A-Za-z0-9_.:-]{1,128}$/;

/** Read the `member` param as a contact id, or `null` for none or a malformed value. */
export function parseMemberParam(raw: string): string | null {
  const id = raw.trim();
  return CONTACT_ID.test(id) ? id : null;
}
